import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  stripePendingSettlementStatusId,
  type StripePendingSettlementStatusId,
} from './StripePendingSettlementStatus.js'

/** Identifier type for public.stripe_pending_settlement_ */
export type StripePendingSettlementCheckoutSessionId = string & {
  __brand: 'StripePendingSettlementCheckoutSessionId'
}

/** Represents the table public.stripe_pending_settlement_ */
export default interface StripePendingSettlementTable {
  /** Database type: pg_catalog.text */
  checkoutSessionId: ColumnType<
    StripePendingSettlementCheckoutSessionId,
    StripePendingSettlementCheckoutSessionId,
    StripePendingSettlementCheckoutSessionId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  stripePaymentIntentId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  serviceContractId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  amountInCents: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  currency: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  status: ColumnType<
    StripePendingSettlementStatusId,
    StripePendingSettlementStatusId | undefined,
    StripePendingSettlementStatusId
  >

  /** Database type: pg_catalog.timestamptz */
  openedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.text */
  openedByEventId: ColumnType<string, string, string>

  /** Database type: pg_catalog.timestamptz */
  resolvedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.text */
  resolvedByEventId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  alertedAt: ColumnType<Date | null, Date | string | null, Date | string | null>
}

export type StripePendingSettlement = Selectable<StripePendingSettlementTable>

export type StripePendingSettlementInitializer =
  Insertable<StripePendingSettlementTable>

export type StripePendingSettlementMutator =
  Updateable<StripePendingSettlementTable>

export const stripePendingSettlementCheckoutSessionId =
  z.string() as unknown as z.Schema<StripePendingSettlementCheckoutSessionId>

export const stripePendingSettlement = z.object({
  checkoutSessionId: stripePendingSettlementCheckoutSessionId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  stripePaymentIntentId: z.string().nullable().optional(),
  serviceContractId: z.string().nullable().optional(),
  amountInCents: z.number().nullable().optional(),
  currency: z.string().nullable().optional(),
  status: stripePendingSettlementStatusId,
  openedAt: z.coerce.date(),
  openedByEventId: z.string(),
  resolvedAt: z.coerce.date().nullable().optional(),
  resolvedByEventId: z.string().nullable().optional(),
  alertedAt: z.coerce.date().nullable().optional(),
}) as unknown as z.Schema<StripePendingSettlement>

export const stripePendingSettlementInitializer = z.object({
  checkoutSessionId: stripePendingSettlementCheckoutSessionId,
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  stripePaymentIntentId: z.string().optional().nullable(),
  serviceContractId: z.string().optional().nullable(),
  amountInCents: z.number().optional().nullable(),
  currency: z.string().optional().nullable(),
  status: stripePendingSettlementStatusId.optional(),
  openedAt: z.coerce.date(),
  openedByEventId: z.string(),
  resolvedAt: z.coerce.date().optional().nullable(),
  resolvedByEventId: z.string().optional().nullable(),
  alertedAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<StripePendingSettlementInitializer>

export const stripePendingSettlementMutator = z.object({
  checkoutSessionId: stripePendingSettlementCheckoutSessionId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  stripePaymentIntentId: z.string().optional().nullable(),
  serviceContractId: z.string().optional().nullable(),
  amountInCents: z.number().optional().nullable(),
  currency: z.string().optional().nullable(),
  status: stripePendingSettlementStatusId.optional(),
  openedAt: z.coerce.date().optional(),
  openedByEventId: z.string().optional(),
  resolvedAt: z.coerce.date().optional().nullable(),
  resolvedByEventId: z.string().optional().nullable(),
  alertedAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<StripePendingSettlementMutator>
